import { Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { RequirementField } from '../data-collection.dto';

interface Props {
    field: RequirementField;
    value: string | number | boolean | null;
    required: boolean;
    disabled?: boolean;
    disabledReason?: string | null;
    /** Surfaced under the input when the most recent save attempt failed. */
    errorMessage?: string | null;
    onChange: (next: string | number | boolean | null) => void;
}

/**
 * Controlled scalar input. Persistence is owned by the parent (DataCollectionTab
 * coordinates the form-level Save), so this component is purely value/onChange.
 */
export function RequirementFieldInput({
    field,
    value,
    required,
    disabled,
    disabledReason,
    errorMessage,
    onChange,
}: Props) {
    const { t } = useTranslation();

    const isMissing = required && (value === null || value === '' || value === undefined);

    if (field.fieldType === 'boolean') {
        return (
            <Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={Boolean(value)}
                            disabled={disabled}
                            onChange={(e) => onChange(e.target.checked)}
                        />
                    }
                    label={
                        <span>
                            {field.label}
                            {required && ' *'}
                        </span>
                    }
                />
                {(errorMessage ?? (disabled ? disabledReason : null)) && (
                    <Typography variant="caption" color={errorMessage ? 'error' : 'text.disabled'} sx={{ ml: 2 }}>
                        {errorMessage ?? disabledReason}
                    </Typography>
                )}
            </Box>
        );
    }

    if (field.fieldType === 'document') {
        return (
            <Box>
                <Typography variant="body2" color="text.secondary">
                    {field.label}
                    {required && ' *'}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                    {t('Document upload not yet available in this view.')}
                </Typography>
            </Box>
        );
    }

    const inputType = field.fieldType === 'number' ? 'number' : field.fieldType === 'date' ? 'date' : 'text';
    // Free-text answers always wrap (render as an auto-growing textarea) so long
    // text doesn't clip at the end of the line when the field is blurred — the
    // user shouldn't have to click in to read it. The backend `multiline` /
    // `minRows` now only tune the starting height; number and date stay single-line.
    const isTextField = inputType === 'text';

    return (
        <TextField
            label={field.label + (required ? ' *' : '')}
            placeholder={field.placeholder ?? undefined}
            helperText={
                errorMessage ??
                (disabled ? (disabledReason ?? undefined) : undefined) ??
                field.description ??
                (isMissing ? t('Required') : undefined)
            }
            error={Boolean(errorMessage) || isMissing}
            type={inputType}
            size="small"
            fullWidth
            disabled={disabled}
            multiline={isTextField}
            minRows={isTextField ? (field.minRows ?? (field.multiline ? 2 : 1)) : undefined}
            value={value === null || value === undefined ? '' : String(value)}
            onChange={(e) => {
                const raw = e.target.value;
                if (field.fieldType === 'number') {
                    onChange(raw === '' ? null : Number(raw));
                } else {
                    onChange(raw);
                }
            }}
            slotProps={{
                inputLabel: { shrink: field.fieldType === 'date' ? true : undefined },
            }}
        />
    );
}
